import axios from 'axios';

/**
 * Factory that creates an Axios instance pre-configured for an API endpoint.
 *
 * Supports two token modes:
 * 1. Manual: call setToken(t) to update the token (original behavior).
 * 2. Dynamic: pass a getToken function that is called on every request.
 *
 * Optional auto-refresh: pass refreshToken and onAuthFailure to automatically
 * retry failed 401/403 requests after refreshing the token.
 *
 * @param {object}   config
 * @param {string}   config.baseURL          - API base URL
 * @param {number}   [config.timeout=10000]
 * @param {Function} [config.getToken]       - Called per-request to get the current token
 * @param {Function} [config.refreshToken]   - Called on 401/403 to obtain a fresh token
 * @param {Function} [config.onAuthFailure]  - Called when refresh fails (e.g. redirect to login)
 * @returns {{ client: AxiosInstance, setToken: Function }}
 */
const createApiClient = ({ baseURL, timeout = 10_000, getToken, refreshToken, onAuthFailure }) => {
  const client = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  let _token = null;

  const setToken = (token) => {
    _token = token;
  };

  client.interceptors.request.use(
    (config) => {
      const token = getToken ? getToken() : _token;
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  client.interceptors.response.use(
    (response) => response.data,
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;
      const message = error.response?.data?.message || error.message || 'Unknown error';

      if ((status === 401 || status === 403) && refreshToken && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const newToken = await refreshToken();
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return client(originalRequest);
        } catch {
          if (onAuthFailure) {
            onAuthFailure();
            return new Promise(() => {});
          }
        }
      }

      if (status === 401) {
        window.dispatchEvent(new CustomEvent('aims:auth:unauthorized'));
      }

      return Promise.reject({ message, status, raw: error });
    },
  );

  return { client, setToken };
};

export default createApiClient;
